<template>
  <div class="externalFinancingForm">
    <Select
      v-model="formData.lender"
      class="mb-2"
      full-width
      label="Lender"
      :options="lenderOptions"
      :field-error="firstErrorMessage(v$.lender)"
    />
    <TextField
      v-if="customLender"
      v-model="formData.customLender"
      data-cy="customLender"
      class="full-width"
      label="Lender Name"
      placeholder="Lender Name"
      name="Lender"
      :error-message="firstErrorMessage(v$.customLender)"
    />
    <TextField
      v-if="!isValidationSupported"
      v-model="formData.loanNumber"
      data-cy="loanNumber"
      class="full-width"
      label="Loan #"
      placeholder="Loan #"
      name="Reference #"
      :error-message="firstErrorMessage(v$.loanNumber)"
    />
    <div v-else class="d-flex align-items-start">
      <TextField
        v-model="formData.loanNumber"
        data-cy="loanNumber"
        class="mr-4 w-50"
        label="Loan #"
        placeholder="Loan #"
        name="Reference #"
        :error-message="firstErrorMessage(v$.loanNumber)"
      />
      <SeraButton
        v-if="showValidateLoanNumber"
        data-cy="validateButton"
        :disabled="isValidating"
        outline
        class="validateLoanNumberButton"
        :text="validatingText"
        @click="validateLoanNumber"
      />
      <div
        v-else
        class="validation-message"
        :class="{ error: lastValidatedState.errorMessage }"
      >
        <div
          v-if="lastValidatedState.errorMessage"
          class="d-flex align-items-center"
        >
          <span class="icon fa-regular fa-triangle-exclamation mr-2" />
          <span data-cy="errorMessage">{{
            lastValidatedState.errorMessage
          }}</span>
        </div>
        <div v-else class="d-flex align-items-center">
          <span class="icon fa-regular fa-badge-check mr-2" />
          <span data-cy="validatedMessage">Validated</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { dollars } from "serviceshift-ui/shared/src/mixins";
import { computed, onMounted, ref } from "vue";
import { useStore } from "vuex";
import { firstErrorMessage } from "@/lib/vuelidate_helpers";

import SeraButton from "@/components/Button.vue";
import TextField from "@/components/TextField.vue";
import { Lenders } from "@/Financing/types";
import { useVuelidate } from "@vuelidate/core";
import { helpers, required, requiredIf } from "@vuelidate/validators";
import usePaymentMethods from "../../usePaymentMethods";

const props = defineProps({
  invoiceId: {
    type: Number,
    default: null
  },
  paymentAmountCents: {
    type: Number,
    default: 0
  }
});

const store = useStore();

const formData = ref({
  lender: "",
  customLender: "",
  loanNumber: ""
});
const hasValidated = ref(false);
const isValidating = ref(false);
const lastValidatedState = ref({
  errorMessage: "",
  loanNumber: "",
  lender: "",
  paymentAmountCents: props.paymentAmountCents
});

const v$ = useVuelidate(
  {
    lender: {
      required: helpers.withMessage("Lender is required", required),
      $autoDirty: true
    },
    customLender: {
      required: helpers.withMessage(
        "Lender name is required",
        requiredIf(() => formData.value.lender === "other")
      ),
      $autoDirty: true
    },
    loanNumber: {
      required: helpers.withMessage("Loan # is required", required),
      $autoDirty: true
    }
  },
  formData.value
);
const { validateFinancingLoanNumber, paymentAmountErrorMessage } =
  usePaymentMethods();

const lenderOptions = [
  {
    label: "Service Finance",
    value: "service_finance"
  },
  {
    label: "Other",
    value: "other"
  }
];

const customLender = computed(() => {
  return formData.value.lender === "other";
});

const serviceFinanceEnabled = computed(() => {
  return store.getters.serviceFinanceEnabled;
});

// Validation is currently disabled until the backend API is fully functional
const isValidationSupported = computed(() => {
  return false;
});

const validatingText = computed(() => {
  if (isValidating.value) return "Validating...";
  return "Validate Loan #";
});

const showValidateLoanNumber = computed(() => {
  return (
    lastValidatedState.value.paymentAmountCents !== props.paymentAmountCents ||
    lastValidatedState.value.lender !== formData.value.lender ||
    lastValidatedState.value.loanNumber !== formData.value.loanNumber
  );
});

onMounted(() => {
  if (serviceFinanceEnabled.value) {
    formData.value.lender = "service_finance";
  }
});

async function validateLoanNumber() {
  await validate();
  isValidating.value = true;
  paymentAmountErrorMessage.value = "";
  try {
    const response = await validateFinancingLoanNumber({
      invoiceId: props.invoiceId,
      loanNumber: formData.value.loanNumber,
      lenderKey: formData.value.lender,
      paymentAmountCents: props.paymentAmountCents
    });
    if (response.result === "valid") {
      lastValidatedState.value = {
        ...formData.value,
        errorMessage: "",
        paymentAmountCents: props.paymentAmountCents
      };
    } else {
      lastValidatedState.value = {
        ...formData.value,
        errorMessage: response.message,
        paymentAmountCents: props.paymentAmountCents
      };
      if (response.message.includes("amount differs")) {
        paymentAmountErrorMessage.value = `${response.message}: ${dollars(
          response.amount_cents
        )}`;
        lastValidatedState.value.errorMessage = "Loan amount mismatch";
      }
    }
  } catch (e) {
    // Do nothing for now
  }
  isValidating.value = false;
  hasValidated.value = true;
}

async function validate() {
  const validated = await v$.value.$validate();
  if (!validated) throw new Error("invalid");
  return true;
}

// NOTE: We need to make sure the processor name is the full lender name.
function getData() {
  return {
    ...formData.value,
    transaction_type: "external_financing",
    processor_name: processorName(),
    reference_id: formData.value.loanNumber
  };
}

function processorName() {
  if (formData.value.lender === "other") {
    return formData.value.customLender;
  } else {
    return Lenders[formData.value.lender].name;
  }
}

defineExpose({ validate, getData });
</script>

<style scoped lang="scss">
.validateLoanNumberButton {
  margin-top: 24px;
}
.validation-message {
  margin-top: 30px;
  .icon {
    font-size: 24px;
  }
  span {
    color: var(--success);
  }
  &.error {
    span {
      color: var(--danger);
    }
  }
}
</style>
